"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import {
  groupBriefsByCategory,
  MAX_VISIBLE_BRIEFS,
} from "../_lib/brief-grouping";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { ExploreBriefCard, FeedDaySection, FeedPageData } from "../types";
import type { BriefCardStyle } from "./assembly/styled-feed-timeline";
import { StyledFeedTimeline } from "./assembly/styled-feed-timeline";
import { CompactBriefRow } from "./compact-brief-row";
import { FeaturedStoryCard } from "./featured-story-card";

const LazyBriefDetailDialog = dynamic(
  async () => {
    const module = await import("./brief-detail-dialog");
    return module.BriefDetailDialog;
  },
  { ssr: false }
);

// ─── Grouped brief list within a day ────────────────────────

function DayBriefs({
  briefs,
  onBriefClick,
}: {
  briefs: ExploreBriefCard[];
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((slug: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((slug: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  if (briefs.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const isCollapsed = collapsedGroups.has(group.categorySlug);
        const isExpanded = expandedGroups.has(group.categorySlug);
        const hasMore = group.briefs.length > MAX_VISIBLE_BRIEFS;
        const remainingCount = group.briefs.length - MAX_VISIBLE_BRIEFS;

        return (
          <div key={group.categorySlug}>
            <button
              className="mb-1.5 flex w-full items-center gap-2 text-left"
              onClick={() => toggleGroup(group.categorySlug)}
              style={{ fontFamily: "var(--homepage-sans-stack)" }}
              type="button"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5 text-[var(--explore-text-muted)]" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-[var(--explore-text-muted)]" />
              )}
              <span className="text-[0.78rem]">{group.categoryEmoji}</span>
              <span className="font-semibold text-[0.75rem] text-[var(--explore-text-primary)]">
                {group.categoryDisplayName}
              </span>
              <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
                ({group.briefs.length})
              </span>
            </button>

            {!isCollapsed && (
              <div>
                <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
                  <div className="divide-y divide-[var(--explore-border-light)]">
                    {group.briefs
                      .slice(0, MAX_VISIBLE_BRIEFS)
                      .map((brief, idx) => (
                        <CompactBriefRow
                          brief={brief}
                          key={brief.briefId}
                          onClick={() => onBriefClick(brief)}
                          positionInList={idx}
                          surface="stories_index_briefs"
                        />
                      ))}
                  </div>
                  {hasMore && (
                    <div
                      className="grid"
                      style={{
                        gridTemplateRows: isExpanded ? "1fr" : "0fr",
                        transition:
                          "grid-template-rows 200ms cubic-bezier(0.23, 1, 0.32, 1)",
                      }}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="divide-y divide-[var(--explore-border-light)] border-[var(--explore-border-light)] border-t">
                          {group.briefs
                            .slice(MAX_VISIBLE_BRIEFS)
                            .map((brief, idx) => (
                              <CompactBriefRow
                                brief={brief}
                                key={brief.briefId}
                                onClick={() => onBriefClick(brief)}
                                positionInList={idx + MAX_VISIBLE_BRIEFS}
                                surface="stories_index_briefs"
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {hasMore && (
                  <div className="-mt-3 mb-1 flex justify-center">
                    <button
                      className="rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg)] px-3 py-1 text-[0.7rem] text-[var(--explore-text-muted)] shadow-sm transition-colors hover:text-[var(--explore-text-primary)]"
                      onClick={() => toggleExpand(group.categorySlug)}
                      style={{ fontFamily: "var(--homepage-sans-stack)" }}
                      type="button"
                    >
                      {isExpanded ? "Show less" : `Show ${remainingCount} more`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Single day section ─────────────────────────────────────

function DaySection({
  section,
  exploreBase,
  onBriefClick,
}: {
  section: FeedDaySection;
  exploreBase: string;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const hasStories = section.stories.length > 0;
  const hasBriefs = section.briefs.length > 0;

  if (!(hasStories || hasBriefs)) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6">
      <div
        className="flex items-center gap-3 px-4 lg:px-0"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <h2 className="font-semibold text-[0.85rem] text-[var(--explore-text-primary)]">
          {section.dateLabel}
        </h2>
        <div className="h-px flex-1 bg-[var(--explore-border-light)]" />
      </div>

      {/* Stories */}
      {hasStories && (
        <div className="flex flex-col gap-3 px-1 lg:px-0">
          {section.stories.map((story) => (
            <FeaturedStoryCard
              exploreBase={exploreBase}
              key={story.storyId}
              story={story}
            />
          ))}
        </div>
      )}

      {/* Briefs */}
      {hasBriefs && (
        <DayBriefs briefs={section.briefs} onBriefClick={onBriefClick} />
      )}
    </section>
  );
}

// ─── Feed timeline ──────────────────────────────────────────

interface FeedTimelineProps {
  data: FeedPageData;
  briefCardStyle?: BriefCardStyle;
}

export function FeedTimeline({ data, briefCardStyle }: FeedTimelineProps) {
  const { exploreBase } = useExploreRoute();
  const [selectedBrief, setSelectedBrief] = useState<ExploreBriefCard | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleBriefClick = useCallback((brief: ExploreBriefCard) => {
    setSelectedBrief(brief);
    setDialogOpen(true);
  }, []);
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const selectedSection = data.sections.find((section) =>
      section.briefs.some(
        (candidate) => candidate.briefId === selectedBrief.briefId
      )
    );
    if (!selectedSection) {
      return;
    }
    const group = groupBriefsByCategory({
      briefs: selectedSection.briefs,
    }).find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [data.sections, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  // When a briefCardStyle is specified, delegate to the styled variant
  if (briefCardStyle) {
    return <StyledFeedTimeline briefStyle={briefCardStyle} data={data} />;
  }

  return (
    <div className="flex flex-col gap-10">
      {data.sections.map((section) => (
        <DaySection
          exploreBase={exploreBase}
          key={section.reportDate}
          onBriefClick={handleBriefClick}
          section={section}
        />
      ))}

      {data.sections.length === 0 && (
        <p
          className="py-12 text-center text-[0.9rem] text-[var(--explore-text-muted)]"
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          No stories or briefs found.
        </p>
      )}

      {selectedBrief ? (
        <LazyBriefDetailDialog
          brief={selectedBrief}
          categoryBriefs={categoryBriefs}
          exploreBase={exploreBase}
          onNavigateBrief={handleNavigateBrief}
          onOpenChange={setDialogOpen}
          open={dialogOpen}
          surface="stories_index_briefs"
        />
      ) : null}
    </div>
  );
}
